/**
 * EventTypesUtil.js
 *
 * Utility functions for handling event types across the application.
 * This centralizes the event type definitions and translations.
 */

// All supported event types in the system (English keys)
export const EVENT_TYPE_KEYS = {
    REHEARSAL: 'rehearsal',
    MASS: 'mass',
    CONCERT: 'concert',
    PARTY: 'party',
    BROADCAST: 'broadcast',
    RECORDING: 'recording',
    MEETING: 'meeting',
    OTHER: 'other'
};

// Default event types to use if no community-specific types are defined
export const DEFAULT_EVENT_TYPES = [
    'rehearsal', 
    'mass', 
    'concert', 
    'meeting', 
    'other'
];

// All possible event types available for configuration in community settings
export const ALL_EVENT_TYPES = [
    'rehearsal',
    'mass',
    'concert',
    'party',
    'broadcast',
    'recording',
    'meeting',
    'other'
];

/**
 * Get the translated display name for an event type
 *
 * @param {string} eventType - Event type key (as stored in database)
 * @param {Function} t - Translation function from useLanguage hook
 * @returns {string} - Translated event type display name
 */
export const getEventTypeDisplayName = (eventType, t) => {
    if (!t) return eventType; // Fallback if translation function not provided
    return t(`events.types.${eventType}`) || eventType;
};

/**
 * Parse supported event types from a community
 * 
 * @param {Object} community - Community object with supported_event_types field
 * @returns {Array} - Array of supported event types, or defaults if parsing fails
 */
export const parseSupportedEventTypes = (community) => {
    if (!community || !community.supported_event_types) {
        return DEFAULT_EVENT_TYPES;
    }
    
    try {
        // Handle both string and already parsed JSON
        if (typeof community.supported_event_types === 'string') {
            return JSON.parse(community.supported_event_types);
        } else if (Array.isArray(community.supported_event_types)) {
            return community.supported_event_types;
        }
    } catch (error) {
    }
    
    return DEFAULT_EVENT_TYPES;
};

/**
 * Get an array of event type options with translations
 * Useful for UI components that need to display dropdown options
 *
 * @param {Function} t - Translation function from useLanguage hook
 * @returns {Array} - Array of event type objects with id and translated name
 */
export const getEventTypeOptions = (t) => {
    if (!t) {
        // Fallback without translations
        return ALL_EVENT_TYPES.map(type => ({ id: type, name: type }));
    }
    return ALL_EVENT_TYPES.map(type => ({
        id: type,
        name: t(`events.types.${type}`)
    }));
};

export default {
    EVENT_TYPE_KEYS,
    DEFAULT_EVENT_TYPES,
    ALL_EVENT_TYPES,
    getEventTypeDisplayName,
    parseSupportedEventTypes,
    getEventTypeOptions
};